import {
  getDownloadURL,
  ref,
  uploadBytesResumable,
  type UploadTaskSnapshot,
} from 'firebase/storage';

import { storage } from '@/auth/firebase';

export interface FirebaseUploadResult {
  downloadUrl: string;
  fileName: string;
  storagePath: string;
}

export interface FirebaseUploadOptions {
  now?: () => number;
  onProgress?: (progress: number, snapshot: UploadTaskSnapshot) => void;
  storagePath?: string;
}

export function sanitizeStorageFileName(fileName: string) {
  return fileName
    .trim()
    .replace(/[/\\?%*:|"<>]/g, '_')
    .replace(/\s+/g, '_')
    .replace(/_+/g, '_') || 'upload';
}

export async function uploadToFirebaseStorage(
  blob: Blob,
  fileName: string,
  options: FirebaseUploadOptions = {},
): Promise<FirebaseUploadResult> {
  const storagePath = options.storagePath ?? 'images/uploads';
  const timestamp = options.now?.() ?? Date.now();
  const sanitizedFileName = sanitizeStorageFileName(fileName);
  const objectPath = `${storagePath.replace(/\/+$/, '')}/kudu_${timestamp}_${sanitizedFileName}`;
  const storageRef = ref(storage, objectPath);

  return new Promise<FirebaseUploadResult>((resolve, reject) => {
    const uploadTask = uploadBytesResumable(storageRef, blob);

    uploadTask.on(
      'state_changed',
      (snapshot) => {
        const progress =
          snapshot.totalBytes > 0
            ? (snapshot.bytesTransferred / snapshot.totalBytes) * 100
            : 0;
        options.onProgress?.(progress, snapshot);
      },
      (error) => {
        reject(error instanceof Error ? error : new Error('Upload failed.'));
      },
      () => {
        void getDownloadURL(uploadTask.snapshot.ref)
          .then((downloadUrl) => {
            resolve({
              downloadUrl,
              fileName,
              storagePath: storageRef.fullPath,
            });
          })
          .catch((error: unknown) => {
            reject(error instanceof Error ? error : new Error('Upload failed.'));
          });
      },
    );
  });
}
